import { Component, ViewEncapsulation, Input, OnDestroy } from '@angular/core';
import { fromEvent, map, of, Subscription, switchMap, tap } from 'rxjs';
import {
	ColDef,
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
	IServerSideDatasource,
	IServerSideGetRowsParams,
	ValueGetterParams,
	ViewportChangedEvent
} from '@ag-grid-community/core';
import { AgGridModule } from '@ag-grid-community/angular';

import { AppWizardService } from '../../../services/app-wizard.service';
import {
	PRODUCT_TYPE_RTPC,
	PRODUCT_TYPE_A2C,
	Offer,
	OfferProduct,
	ProposedProduct
} from '../../../models/offer';
import { UNDERSCORE } from '../../../../shared/utils/constant';
import { pageTransitions } from '../../../../shared/animations/page-transitions';
import { CACHE_BLOCK_SIZE_TABLE, CELL_WIDTH_LARGE, CELL_WIDTH_MEDIUM,
	CELL_WIDTH_SMALL, getUserPivotFields } from '../../utils/product-table/product-info';
import { CommonGridOptions, serverSideAgGridModules } from '../../../../shared/datacenter/grid-options';
import { TranslationService } from '../../../../shared/services/translation.service';
import { CheckboxHeaderComponent } from '../../../../shared/datacenter/header-components/checkbox-header.component';
import { CommonColDef, IconLinkColDef, NumericColDef } from '../../../../shared/datacenter/column-defs';
import {
	NAMESPACE_A2C_FIELD,
	ProductCharacteristicsTranslationService
} from '../../../../shared/services/product-characteristics-translation.services';
import { FN_COMMERCIAL_STEEL_GRADE_ID, FN_LENGTH_UNIT,
	FN_LENGTH_VALUE, FN_THICKNESS_UNIT, FN_THICKNESS_VALUE,
	FN_WIDTH_UNIT, FN_WIDTH_VALUE, getFieldFormat, getFieldUnit,
	isFieldUnit } from '../../../models/product-descriptor-a2c';
import { ValueFormatType } from '../../../models/value-format';
import { createProductCardUrl } from '../product-card/create-product-card-url';
import { CURRENCY_VALUES_SYMBOLE } from '../../../models/list-cst';
import { formatDimension } from '../../../../shared/utils/number.format';
import { ProductService } from '../../../services/product.api.service';
import { UserService } from '../../../../shared/services/user.service';
import { AdycoSettingUser } from '../../../models/adyco';
import { safe } from '../../../../shared/utils/safe';

import { Product } from './offer-product';

const resizeObservable = fromEvent(globalThis, 'resize');
class ProductDatasource implements IServerSideDatasource {
	newRows?: Product[];
	newRowsObj?: Product;
	currency?: string;
	pricingUnit?: string;
	offerType?: string;
	adycoSetting: AdycoSettingUser = {
		originalSetting: {
			availableFields: [],
			pivotFields: []
		},
		userPivotFields: []
	};

	soldTo = '';
	shipTo = '';
	endUser = '';
	category = '';
	sub: Subscription | undefined;
	adecoProducts: ProposedProduct[] | undefined;
	selectedProducts: OfferProduct | undefined;
	headerMaxLength = 25;
	loading = false;

	constructor(
		private readonly translationService: TranslationService,
		private readonly appWizardService: AppWizardService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private fieldNames: string[],
		private readonly gridApi: GridApi<Product>,
		private readonly offer: Offer,
		private readonly offerProducts: OfferProduct[],
		private readonly productService: ProductService,
		private readonly userService: UserService
	) {
		this.currency = this.offer.header.currency || '';
		this.pricingUnit = this.offer.header.pricingUnit || '';
		this.soldTo = this.offer.header.soldTo || '';
		this.shipTo = this.offer.header.shipTo || '';
		this.endUser = this.offer.header.endUser || '';
		this.offerType = this.offer.header.offerType || '';
		this.createRowProduct();
	}

	destroy() {
		if (this.sub) {
			this.sub.unsubscribe();
		}
	}

	createRowProduct() {
		this.newRows = [];
		for (const formProduct of safe(this.offerProducts)) {
			const { family } = formProduct.description;
			// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
			if (family) {
				this.newRowsObj = {
					description: {
						productRef: formProduct.description.productRef,
						configurationRef: formProduct.description.configurationRef,
						productRequestId: formProduct.description.productRequestId
					},
					steelGrade: formProduct.description.steelGrade,
					dimension: formatDimension(
						formProduct.description.dimension.thickness.value,
						formProduct.description.dimension.width.value,
						formProduct.description.dimension.length ? formProduct.description.dimension.length.value : ''
					),
					quantity: formProduct.quantity,
					src: formProduct,
					price: formProduct.price ? {
						amount: formProduct.price,
						currency: this.currency ? this.getCurrencyValue(this.currency) : UNDERSCORE,
						pricingUnit: this.pricingUnit
					} : undefined,
					status: formProduct.offerStatus,
					familyCode: family.code || ''
				};
				this.newRows.push(this.newRowsObj);
				if (family.code) {
					this.sub = this.loadAvailableFields(family.code)
						.pipe(switchMap(() => this.loadProductTable(family.code)))
						.subscribe({
							next: productTable => {
								this.adecoProducts = productTable.products;
								for (const product of this.adecoProducts) {
									if (product.description.productRequestId) {
										this.selectedProducts = this.offerProducts.find(x =>
											x.description.productRequestId === product.description.productRequestId);
										if (this.selectedProducts !== undefined) {
											this.selectedProducts.characteristics = product.characteristics;
										}
									}
								}
								if (this.selectedProducts) {
									this.offerProducts.push(this.selectedProducts);
								}
								if (this.newRows) {
									this.createColumnDefinitions(
										this.newRows,
										productTable.selectedFieldNames
									);
								}
								this.fieldNames = productTable.selectedFieldNames;
								this.loading = false;
							},
							error: () => {
								this.loading = false;
							}
						});
				}
			}
		}
	}

	loadProductTable(selectedFamilyCode: string) {
		return this.productService.getProductTableOrderBookOnly(
			this.soldTo,
			this.shipTo,
			this.endUser,
			selectedFamilyCode,
			60,
			this.adycoSetting.userPivotFields,
			false,
			this.userService.user.getSettings('offer_adyco')?.value.offerAdyco[0].isToggle,
			'INLINE'
		).pipe(tap({
			next: productTable => {
				this.fieldNames = productTable.selectedFieldNames;
			},
			error: (err: unknown) => {
				console.error('unable to load products', err);
			}
		}));
	}

	getCurrencyValue(currency: string) {
		const i = CURRENCY_VALUES_SYMBOLE.findIndex(x => x.code === currency);
		return i === -1 ? UNDERSCORE : CURRENCY_VALUES_SYMBOLE[i].code;
	}

	getRows(params: IServerSideGetRowsParams<Product>) {
		if (this.newRows) {
			this.createColumnDefinitions(this.newRows, this.fieldNames);
			params.success({ rowData: this.newRows, rowCount: this.newRows.length });
		}
	}

	refreshChangedValue() {
		const params = { force: true };
		this.gridApi.refreshCells(params);
	}

	refresh() {
		this.gridApi.onSortChanged();
	}

	setFocus(rowIndex: number, colKey: string) {
		this.gridApi.setFocusedCell(rowIndex, colKey);
	}

	private createColumnDefinitions(products: Product[], fieldNames: string[]) {
		const columnDefs = [];
		columnDefs.push(
			new IconLinkColDef<Product>({
				headerName: this.translationService.getTR('product.table.header.view'),
				minWidth: 50,
				maxWidth: 50,
				width: 50,
				headerComponent: CheckboxHeaderComponent,
				headerClass: ['copo-offer-product-card-grid'],
				cellRendererParams: {
					icon: 'icon_view',
					tooltipMsg: '',
					routerLink(params: ICellRendererParams<Product, unknown>) {
						return params.data ? { commands: [createProductCardUrl(params.data)],
							extras: {
								state: {
									productRef: params.data.description.productRef,
									configurationRef: params.data.description.configurationRef,
									productType: params.data.description.configurationRef
										? PRODUCT_TYPE_RTPC
										: PRODUCT_TYPE_A2C,
								}
							} } : undefined;
					}
				},
				tooltipValueGetter: () => this.translationService.getTR('eoffer.product.card'),
				pinned: 'left'
			}),
			new CommonColDef<Product>({
				headerName: this.translationService.getTR('eoffer.table.header.status'),
				minWidth: CELL_WIDTH_MEDIUM,
				maxWidth: CELL_WIDTH_MEDIUM,
				width: CELL_WIDTH_MEDIUM,
				sortable: false,
				headerComponent: CheckboxHeaderComponent,
				headerClass: ['copo-offer-product-card-grid'],
				valueGetter: (params: ValueGetterParams) => {
					if (!params.data) {
						return '';
					}
					const product = <Product> params.data;
					return product.status.toUpperCase();
				},
			})
		);

		// quantity
		if (this.offerType === 'spot') {
			columnDefs.push(new CommonColDef<Product>({
				headerName: this.translationService.getTR('offer.card.table.header.quantity'),
				minWidth: 100,
				maxWidth: 100,
				width: 100,
				sortable: false,
				headerComponent: CheckboxHeaderComponent,
				headerClass: ['copo-offer-product-card-grid'],
				// eslint-disable-next-line @typescript-eslint/naming-convention
				cellStyle: { 'font-weight': 'bold' },
				valueGetter: params => {
					if (!params.data) {
						return '';
					}
					const product = params.data;
					return product.quantity;
				}
			}));
		}

		// Price
		columnDefs.push(new CommonColDef<Product>({
			headerName: this.translationService.getTR('eoffer.table.header.price'),
			minWidth: 100,
			maxWidth: CELL_WIDTH_MEDIUM,
			width: CELL_WIDTH_MEDIUM,
			sortable: false,
			headerComponent: CheckboxHeaderComponent,
			headerClass: ['copo-offer-product-card-grid'],
			// eslint-disable-next-line @typescript-eslint/naming-convention
			cellStyle: { 'font-weight': 'bold' },
			valueGetter: params => {
				if (!params.data) {
					return '';
				}
				const product = params.data;
				if (product.price?.amount && product.price.currency) {
					return `${ String(product.price.amount) } ${ String(product.price.currency) } / 
						${ String(product.price.pricingUnit) }`;
				}
				return product.price?.amount;
			}
		}));

		// steel grade
		{
			const isAdycoField = this.adycoSetting.userPivotFields.includes(FN_COMMERCIAL_STEEL_GRADE_ID);
			const adycoFieldMarker = isAdycoField ? '*' : '';
			columnDefs.push(new CommonColDef<Product>({
				headerName: `${ adycoFieldMarker }${
					this.translationService.getTR('eoffer.table.header.steelGrade') }`,
				minWidth: CELL_WIDTH_LARGE,
				sortable: false,
				headerComponent: CheckboxHeaderComponent,
				headerClass: ['copo-offer-product-card-grid'],
				valueGetter: params => {
					if (!params.data) {
						return '';
					}
					const product = params.data;
					return product.steelGrade;
				},
			}));
		}

		// dimension
		{
			const isAdycoField = this.adycoSetting.userPivotFields.includes(FN_THICKNESS_VALUE)
				|| this.adycoSetting.userPivotFields.includes(FN_WIDTH_VALUE)
				|| this.adycoSetting.userPivotFields.includes(FN_LENGTH_VALUE);
			const adycoFieldMarker = isAdycoField ? '*' : '';
			columnDefs.push(new CommonColDef<Product>({
				headerName: `${ adycoFieldMarker }${
					this.translationService.getTR('eoffer.table.header.dimension') }`,
				minWidth: CELL_WIDTH_LARGE,
				maxWidth: CELL_WIDTH_LARGE,
				width: CELL_WIDTH_LARGE,
				sortable: false,
				headerComponent: CheckboxHeaderComponent,
				headerClass: ['copo-offer-product-card-grid'],
				valueGetter: params => !params.data ? '' : params.data.dimension,
			}));
		}

		const remainingFields = fieldNames.filter(fieldName => !(
			isFieldUnit(fieldName)
			|| fieldName === FN_THICKNESS_VALUE || fieldName === FN_THICKNESS_UNIT
			|| fieldName === FN_WIDTH_VALUE || fieldName === FN_WIDTH_UNIT
			|| fieldName === FN_LENGTH_VALUE || fieldName === FN_LENGTH_UNIT
			|| fieldName === FN_COMMERCIAL_STEEL_GRADE_ID
		));
		// dynamic fields
		this.getDynamicFields(remainingFields, products, columnDefs);
		this.gridApi.setGridOption('columnDefs', <ColDef<Product>[]> columnDefs);
	}

	private getDynamicFields(remainingFields: string[], products: Product[], columnDefs: CommonColDef<Product>[]) {
		for (const fieldName of remainingFields) {
			const format = getFieldFormat(fieldName);
			const isFormatNumber = format !== undefined && format.type === ValueFormatType.TYPE_NUMBER;
			const fieldUnitName = getFieldUnit(fieldName);
			const commonUnit = fieldUnitName ? this.computeCommonUnit(products, fieldUnitName) : undefined;
			const translatedCommonUnit = commonUnit
				? ` (${ this.productCharacteristicsTranslationService.translate(commonUnit) })` : '';
			const isAdycoField = this.adycoSetting.userPivotFields.includes(fieldName);
			const adycoFieldMarker = isAdycoField ? '*' : '';
			const translatedFieldName = this.productCharacteristicsTranslationService
				.translate(`${ NAMESPACE_A2C_FIELD }${ fieldName }`);
			const headerName = `${ adycoFieldMarker }${ translatedFieldName }${ translatedCommonUnit }`;
			const cellWidth = this.getCharacteristicCellWidth(products, fieldName, headerName);
			if (!isFormatNumber) {
				columnDefs.push(new CommonColDef<Product>({
					headerName,
					minWidth: cellWidth,
					autoHeaderHeight: headerName.length > this.headerMaxLength,
					sortable: false,
					headerComponent: CheckboxHeaderComponent,
					headerClass: ['copo-offer-product-card-grid'],
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const product = params.data;
						return product.src.characteristics ? product.src.characteristics[fieldName] : '';
					},
				}));
			} else {
				columnDefs.push(new NumericColDef<Product>({
					headerName,
					minWidth: cellWidth,
					autoHeaderHeight: headerName.length > this.headerMaxLength,
					sortable: false,
					headerComponent: CheckboxHeaderComponent,
					headerClass: ['copo-offer-product-card-grid'],
					valueGetter: params => {
						if (!params.data) {
							return '';
						}
						const product = params.data;
						return product.src.characteristics ? Number(product.src.characteristics[fieldName]) : '';
					},
				}));
			}
		}
	}

	private getCharacteristicCellWidth(
		products: Product[],
		characName: string,
		characTitle: string
	) {
		const maxSize = Math.max(this.getMaxValueSize(products, characName), characTitle.length);
		if (maxSize <= 9) {
			return CELL_WIDTH_SMALL;
		}
		if (maxSize <= 18) {
			return CELL_WIDTH_MEDIUM;
		}
		return CELL_WIDTH_LARGE;
	}

	private computeCommonUnit(
		products: Product[],
		unitFieldName: string
	) {
		let isFirst = true;
		let commonUnit: string | undefined;
		for (const product of products) {
			const unit = product.src.characteristics ? product.src.characteristics[unitFieldName] : undefined;
			if (!unit) {
				continue;
			}
			if (isFirst) {
				isFirst = false;
				commonUnit = unit;
			} else if (commonUnit !== unit) {
				return undefined;
			}
		}
		return commonUnit !== undefined ? this.productCharacteristicsTranslationService
			.translate(commonUnit) : undefined;
	}

	private getMaxValueSize(
		products: Product[],
		characName: string
	) {
		let max = 0;
		for (const product of products) {
			const value = product.src.characteristics ? product.src.characteristics[characName] : undefined;
			const valueSize = value ? this.productCharacteristicsTranslationService.translate(value).length : 0;
			if (valueSize > max) {
				max = valueSize;
			}
		}
		return max;
	}

	private loadAvailableFields(
		selectedFamilyCode: string
	) {
		const { adycoFields } = this.appWizardService.getState().ui;
		this.category = selectedFamilyCode.split('#')[0].trim();
		if (adycoFields) {
			return of(this.updateAdycoSetting(selectedFamilyCode, adycoFields));
		}
		return this.productService.getAdycoFields(this.category).pipe(map(
			adycoFieldsLoaded => {
				this.appWizardService.getState().ui.adycoFields = adycoFieldsLoaded.productCharacteristics;
				this.appWizardService.getState().ui.defaultFields = adycoFieldsLoaded.defaultCharacterristics;
				return this.updateAdycoSetting(selectedFamilyCode, adycoFieldsLoaded.productCharacteristics);
			}
		));
	}

	private updateAdycoSetting(
		selectedFamilyCode: string,
		adycoFields: string[]
	) {
		const pivots = getUserPivotFields(
			this.userService.user.getSettings('offer_adyco'),
			this.offer.header.soldTo || '',
			selectedFamilyCode,
			this.appWizardService
		);
		const pivotsCopy = [];
		for (const pivot of pivots) {
			pivotsCopy.push(pivot);
		}
		this.adycoSetting = {
			originalSetting: {
				availableFields: adycoFields,
				pivotFields: pivotsCopy
			},
			userPivotFields: pivots
		};
		return this.adycoSetting;
	}
}

@Component({
	selector: 'copo-offer-offer-product-card',
	templateUrl: './offer-product-card.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [AgGridModule]
})
export class OfferProductCardComponent implements OnDestroy {
	@Input() offer!: Offer;
	@Input() offerProducts!: OfferProduct[];
	@Input() characteristicNames?: string[];
	@Input() fieldNames?: string[];

	readonly gridOptions: CommonGridOptions<Product>;

	readonly underscore = UNDERSCORE;
	isLoading = false;
	readonly agGridModules = serverSideAgGridModules;
	productDatasource?: ProductDatasource;
	resizeSubscription?: Subscription;

	constructor(
		private readonly translationService: TranslationService,
		private readonly appWizardService: AppWizardService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly productService: ProductService,
		private readonly userService: UserService
	) {
		this.gridOptions = new CommonGridOptions<Product>({
			translationService: this.translationService,
			rowModelType: 'serverSide',
			multiSortKey: 'ctrl',
			cacheBlockSize: CACHE_BLOCK_SIZE_TABLE,
			suppressRowClickSelection: true,
			rowSelection: 'multiple',
			columnDefs: [],
			domLayout: 'autoHeight',
		});
	}

	ngOnDestroy() {
		if (this.resizeSubscription) {
			this.resizeSubscription.unsubscribe();
		}
	}

	onGridReady(params: GridReadyEvent<Product>) {
		const productDatasource = new ProductDatasource(
			this.translationService,
			this.appWizardService,
			this.productCharacteristicsTranslationService,
			this.characteristicNames || [],
			params.api,
			this.offer,
			this.offerProducts,
			this.productService,
			this.userService
		);
		this.productDatasource = productDatasource;
		params.api.setGridOption('serverSideDatasource', productDatasource);
		params.api.sizeColumnsToFit();
		this.resizeSubscription = resizeObservable.subscribe(() => {
			params.api.sizeColumnsToFit();
		});
	}

	onViewportChanged(params: ViewportChangedEvent) {
		params.api.sizeColumnsToFit();
	}
}
